"use client";

import { chunk } from "@/utils/chunk";
import {
  MAX_IMAGES_PER_SECTION,
  GALLERY_KEYS_TO_ID,
} from "@/constants/gallery-images";
import { useMainGalleryQueries } from "./use-main-gallery-queries";
import type { Image } from "@/types/image.types";

type GalleryKey = keyof typeof GALLERY_KEYS_TO_ID;
type GalleryValues = (typeof GALLERY_KEYS_TO_ID)[GalleryKey];

export function useGalleryImages(images: Image[]) {
  const { mainGallery } = useMainGalleryQueries();

  const { filter, destination } = mainGallery;

  const allowedCategories = (() => {
    if (filter === "all" && destination === "all") {
      return Object.values(GALLERY_KEYS_TO_ID);
    }

    if (filter === "all") {
      return Object.entries(GALLERY_KEYS_TO_ID)
        .filter(([key]) => key.endsWith(`-${destination}`))
        .map(([, id]) => id);
    }

    if (destination === "all") {
      return Object.entries(GALLERY_KEYS_TO_ID)
        .filter(([key]) => key.startsWith(`${filter}-`))
        .map(([, id]) => id);
    }

    const galleryKey = `${filter}-${destination}` as GalleryKey;
    const galleryId = GALLERY_KEYS_TO_ID[galleryKey];

    return galleryId !== undefined ? [galleryId] : [];
  })();

  const filteredImages = (() => {
    if (filter === "all" && destination === "all") {
      return images;
    }

    if (allowedCategories.length === 0) {
      return [];
    }

    return images.filter((image) => {
      if (!image.category) {
        return false;
      }

      const category = Number(image.category) as unknown as GalleryValues;

      return allowedCategories.includes(category);
    });
  })();

  const chunkedImages = chunk(filteredImages, MAX_IMAGES_PER_SECTION);

  return chunkedImages;
}
